import * as React from 'react';
import { useTranslation } from 'next-i18next';
import tw from 'twin.macro';

import { BaseModal, ButtonV2, ButtonProps } from '@restworld/ui-ds';

export interface IFormModalWrapper {
  loading?: boolean;
  onSubmitHandler?: () => void;
  onCancelHandler?: () => void;
  modalVisible?: boolean;
  onModalClose?: () => void;
  title?: string;
  subTitle?: string;
  submitHandlerProps?: Omit<ButtonProps, 'variant'>;
  headerIcon?: React.ReactNode;
  onCancelHandlerProps?: Omit<ButtonProps, 'variant'>;
}

export const ModalToggleWrapper = ({
  children,
  loading,
  onSubmitHandler,
  onCancelHandler,
  modalVisible,
  onModalClose,
  title,
  subTitle,
  submitHandlerProps,
  headerIcon,
  onCancelHandlerProps,
}: React.PropsWithChildren<IFormModalWrapper>) => {
  const { t } = useTranslation('user-settings');

  const footer = React.useMemo(
    () => (
      <div tw="flex flex-col gap-5 w-full">
        <ButtonV2 loading={loading} fullwidth onClick={onSubmitHandler} {...submitHandlerProps}>
          {t('confirm')}
        </ButtonV2>
        {(onCancelHandler || onCancelHandlerProps?.onClick) && (
          <ButtonV2 fullwidth variant="tertiary" onClick={onCancelHandler} {...onCancelHandlerProps}>
            {t('cancel')}
          </ButtonV2>
        )}
      </div>
    ),
    [loading, onCancelHandler, onCancelHandlerProps, onSubmitHandler, submitHandlerProps, t]
  );
  const header = React.useMemo(
    () => (
      <div tw="flex flex-col gap-8 items-center px-6">
        {headerIcon ?? ''}
        <h2 tw="text-xl leading-7.5 text-neutral-700 font-semibold text-center">{title}</h2>
        <h5 tw="text-base font-medium text-neutral-200 text-center">{subTitle}</h5>
      </div>
    ),
    [headerIcon, subTitle, title]
  );
  return (
    <BaseModal
      open={modalVisible}
      onClose={onModalClose}
      modalStyles={[tw`md:max-w-md`]}
      footer={footer}
      title={header}
    >
      <Form action="#!" method="post" onSubmit={(e) => e.preventDefault()}>
        {children}
      </Form>
    </BaseModal>
  );
};

const Form = tw.form`flex flex-col gap-8 pb-12 pt-4 w-full`;
